import { Box, Typography, Button, TextField } from "@mui/material";
import { makeSxStyles } from "ui";
import CustomerNote from "./customer-note";
import { ArrowBack, SendOutlined } from "@mui/icons-material";
import { SelectedAddressForNotes } from "../../../components/map/selected-addresses-sidebar";
import useRoutePlannerStore from "../../../state/route-planner-store";
import { useLocationNotes } from "../../../hooks/use-location-notes";
import useRegionManagementStore from "../../../state/region-management-store";

type SidebarNotesViewProps = {
  onCloseNotes: () => void;
  selectedAddressForNotes: SelectedAddressForNotes;
};

const SidebarNotesView = ({
  onCloseNotes,
  selectedAddressForNotes,
}: SidebarNotesViewProps) => {
  const { updateSelectedAddresses, selectedAddresses } = useRoutePlannerStore();
  const {
    updateSelectedAddresses: updateSelectedAddressesRegionManagement,
    selectedAddresses: selectedAddressesRegionManagement,
  } = useRegionManagementStore();
  const styles = useStyles();

  const updateNotesCounter = (locationNoteCount: number | undefined = 0) => {
    const counter = locationNoteCount >= 0 ? locationNoteCount : 0;
    const updatedSelectedAddresses = selectedAddresses.map((selected) => {
      if (selected.id === selectedAddressForNotes.id) {
        return {
          ...selected,
          locationNoteCount: counter,
        };
      }
      return selected;
    });

    const updatedSelectedAddressesRegionManagement =
      selectedAddressesRegionManagement.map((selected) => {
        if (selected.id === selectedAddressForNotes.id) {
          return {
            ...selected,
            locationNoteCount: counter,
          };
        }
        return selected;
      });
    updateSelectedAddresses(updatedSelectedAddresses);
    updateSelectedAddressesRegionManagement(
      updatedSelectedAddressesRegionManagement,
    );
  };

  const {
    data,
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
    submitForm,
    handleDeleteNote,
    handleEditNote,
    isEditNotePending,
  } = useLocationNotes({
    locationId: selectedAddressForNotes.id || "",
    onCreateSuccess: () => updateNotesCounter((data?.data?.length || 0) + 1),
    onDeleteSuccess: () => updateNotesCounter((data?.data?.length || 0) - 1),
    refetchOptions: {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 0,
    },
  });

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Button
          sx={{
            minWidth: "40px",
            height: "40px",
          }}
          onClick={onCloseNotes}
        >
          <ArrowBack />
        </Button>
        <Typography variant="titleMedium" textOverflow="ellipsis" noWrap>
          {selectedAddressForNotes.address}
        </Typography>
      </Box>

      <Typography
        variant="titleMedium"
        color="#8ACEFF"
        paddingTop={2}
        paddingX={2}
      >
        Notes
      </Typography>

      <Box sx={styles.notesSection}>
        {data?.data?.map((customerNote) => (
          <CustomerNote
            key={customerNote.id}
            note={customerNote}
            onDelete={() => handleDeleteNote(customerNote.id)}
            onBellClick={() =>
              handleEditNote(customerNote.id, {
                mustRead: customerNote.mustRead ? false : true,
              })
            }
            onContentEdit={(newContent) =>
              handleEditNote(customerNote.id, {
                content: newContent,
              })
            }
            isEditNotePending={isEditNotePending}
          />
        ))}
      </Box>

      <Box sx={styles.inputField}>
        <TextField
          placeholder="Add Note"
          name="content"
          id="content"
          type="text"
          value={values.content}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.content && (errors.content as string)}
          error={touched.content && !!errors.content}
          margin="dense"
          variant="outlined"
          fullWidth
          multiline
          rows={2}
          InputProps={{
            endAdornment: (
              <SendOutlined
                sx={{
                  ...styles.sendIcon,
                  opacity: !values.content ? 0.5 : 1,
                  cursor: !values.content ? "default" : "pointer",
                }}
                onClick={values.content ? submitForm : undefined}
              />
            ),
          }}
          sx={styles.textField}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeSxStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  notesSection: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    gap: "16px",
  },
  inputField: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },
  textField: {
    "& svg": {
      alignSelf: "flex-end",
    },
  },
  sendIcon: {
    cursor: "pointer",
  },
}));

export default SidebarNotesView;
